import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Users, Clock, Award, ArrowRight, MessageCircle } from 'lucide-react';
import { siteManagementCourses, heroImages, companyInfo } from '../data/mock';

const SiteManagementPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={heroImages.main} 
            alt="Site Management Training" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              SSSTS & SMSTS Courses
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              CITB-approved site safety training for supervisors and managers
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              Book Your Course
            </a>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-6">
              Site Safety Management Training
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                SSSTS (Site Supervisors Safety Training Scheme) and SMSTS (Site Managers Safety Training Scheme) are essential qualifications for anyone supervising or managing construction sites in the UK.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>These CITB-approved courses are mandatory for construction supervisors and managers</strong> and demonstrate your knowledge of health, safety, welfare, and environmental management on construction sites.
              </p>
              
              <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg my-8">
                <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                  Why These Qualifications Matter:
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Legal requirement for supervisory roles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>CITB Grant funding available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Career progression to management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Higher earning potential</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Industry-recognized qualification</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Comparison */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
              Choose Your Course
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Select the right qualification for your supervisory or management role
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {siteManagementCourses.map((course, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-8 border-2 ${
                  course.popular 
                    ? 'border-construction-yellow shadow-xl' 
                    : 'border-gray-200 shadow-sm'
                } hover:shadow-xl transition-all relative`}
              >
                {course.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-construction-yellow text-construction-darkblue px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-construction-yellow/10 p-3 rounded-lg">
                      <Users className="text-construction-yellow" size={32} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-construction-darkblue">
                        {course.name}
                      </h3>
                      <p className="text-construction-yellow font-semibold">
                        {course.level}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {course.fullName}
                  </p>
                </div>

                <div className="flex items-center gap-8 mb-6 pb-6 border-b border-gray-200">
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-bold text-construction-darkblue flex items-center gap-1 text-lg">
                      <Clock size={18} />
                      {course.duration}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Price</p>
                    <p className="font-bold text-construction-darkblue text-2xl">
                      {course.price}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-construction-darkblue mb-3 flex items-center gap-2">
                    <Award size={20} className="text-construction-yellow" />
                    Course Features:
                  </h4>
                  <ul className="space-y-2">
                    {course.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold text-construction-darkblue mb-2 text-sm">
                    Who Should Attend:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {course.whoFor.map((role, idx) => (
                      <span
                        key={idx}
                        className="bg-white border border-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>

                <a
                  href={`tel:${companyInfo.phone}`}
                  className={`block text-center px-6 py-3 rounded-lg font-bold transition-all hover:scale-105 ${
                    course.popular
                      ? 'bg-construction-yellow text-construction-darkblue hover:bg-yellow-500'
                      : 'bg-construction-darkblue text-white hover:bg-black'
                  }`}
                >
                  Book {course.name}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-12 text-center">
              What You'll Learn
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-construction-darkblue mb-6">
                  SSSTS Course Content
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Health & safety law and responsibilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Risk assessments and method statements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Accident prevention and reporting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Site inductions and toolbox talks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Monitoring and supervising site activities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Environmental awareness</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Occupational health issues</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-construction-darkblue mb-6">
                  SMSTS Course Content
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Advanced health & safety legislation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Site management planning and control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Leadership and communication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">CDM regulations compliance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Welfare facilities and first aid</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Environmental management systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Occupational health management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Plant, equipment and temporary works</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                Assessment & Certification
              </h3>
              <p className="text-gray-700">
                Both courses include continuous assessment throughout and a final exam on the last day. Upon successful completion, you'll receive a CITB certificate valid for 5 years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-construction-darkblue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Advance Your Construction Career
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your SSSTS or SMSTS course today and step into a supervisory or management role
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              {companyInfo.phone}
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in SSSTS/SMSTS courses`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105"
            >
              Book Your Course
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SiteManagementPage;
